import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { STORAGE_KEYS } from '../../../../../shared/constants/storage-keys';
import { ReviewRequest } from '../../../../../shared/models/dtc/reviewRequest';
import { Decision } from '../../../../../shared/models/internal/decision';
import { SelectionOptionValue } from '../../../../../shared/models/internal/selection-option-value';
import { errorToString, pascalCaseToWords } from '../../../../common/common-utils';
import { ReviewRequestApiService } from '../../../../services/api/review-request-api.service';
import { AppInsightsService } from '../../../../services/app-insights.service';
import { UnsavedChangesTrackerService } from '../../../../services/unsaved-changes-tracker.service';
import { SelectionComponent } from '../../selection/selection.component';
import { ThumbUpIconComponent } from '../../../icons/thumb-up-icon/thumb-up-icon.component';
import { DecisionModalComponent } from '../../../modals/decision-modal/decision-modal.component';
import { ReviewRequestFields } from './review-request-fields/review-request-fields';
import { Reviewable } from '../../../../../shared/models/base/reviewable';
import { CreateReviewPropagationModal } from '../../../modals/propagation-modal/create-review-propagation-modal';
import { UpdateReviewPropagationModal } from '../../../modals/propagation-modal/update-review-propagation-modal';
import { ExecutionType } from '../../../modals/propagation-modal/execution-type';
import { DeleteReviewPropagationModal } from '../../../modals/propagation-modal/delete-review-propagation-modal';
import { ApproveDtcSiblingsModal } from '../../../modals/propagation-modal/approve-dtc-propagation-modal';
import { AccessRightsService } from '../../../../services/access-rights.service';
import { Policy } from '../../../../../shared/models/info/policy';
import { ReviewerRole } from '../../../../../shared/enums/reviewer-role';
import { AgentActionDirective } from '../../../../directives/agent-action.directive';
import { HotToastService } from '@ngxpert/hot-toast';

@Component({
  selector: 'app-review-request-edit',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectionComponent,
    ThumbUpIconComponent,
    DecisionModalComponent,
    ReviewRequestFields,
    DeleteReviewPropagationModal,
    UpdateReviewPropagationModal,
    CreateReviewPropagationModal,
    ApproveDtcSiblingsModal,
    AgentActionDirective,
  ],
  templateUrl: './review-request-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './review-request-edit.component.scss',
})
export class ReviewRequestEditComponent implements OnInit, OnChanges {
  @Input() public reviewable?: Reviewable;
  @Output() public reviewableUpdated = new EventEmitter<Reviewable>();
  @ViewChild('discardReviewDecisionModal') public discardReviewDecisionModal!: DecisionModalComponent;
  @ViewChild('createReviewPropagationModal') public createReviewPropagationModal!: CreateReviewPropagationModal;
  @ViewChild('updateReviewPropagationModal') public updateReviewPropagationModal!: UpdateReviewPropagationModal;
  @ViewChild('deleteReviewPropagationModal') public deleteReviewPropagationModal!: DeleteReviewPropagationModal;
  @ViewChild('approveSiblingsModal') public approveSiblingsModal!: ApproveDtcSiblingsModal;

  public reviewRoles: SelectionOptionValue[] = [];

  public editReviewEnabled = true;
  public displayedReview?: ReviewRequest;
  public canApprove = true;
  public createReviewDisabled = true;
  public showCreateButton = false;
  public displayHintReviewForOtherRoleExists = false;

  protected reviewerRole?: string;
  protected readonly executionType = ExecutionType;

  private readonly reviewRequestApi = inject(ReviewRequestApiService);
  private readonly toast = inject(HotToastService);
  private readonly appInsightsService = inject(AppInsightsService);
  private changesSubscription?: Subscription;
  private readonly unsavedChangesService = inject(UnsavedChangesTrackerService);
  private accessRightsService = inject(AccessRightsService);

  private reviewToDiscard?: ReviewRequest;
  private previousReviewerRole?: string;
  private afterSalesRoleEnabled = false;
  private technicalOrganisationRoleEnabled = false;

  public ngOnInit(): void {
    this.displayedReview = undefined;
    this.fetchReviewRoles();
    this.accessRightsService.getAccessRights$().subscribe((ar) => {
      this.afterSalesRoleEnabled = ar.authorized(Policy.canCreateAfterSalesDtcReviewRequests);
      this.technicalOrganisationRoleEnabled = ar.authorized(Policy.canCreateTechnicalOrganisationDtcReviewRequests);
    });

    this.appInsightsService.logSidebarUsage('review_requests');
  }

  public ngOnChanges(): void {
    this.init();
  }

  public upsertReview(review: ReviewRequest): void {
    if (!review) {
      return;
    }

    review.reviewerRole = this.reviewerRole;

    if (review.id?.length ?? 0 > 0) {
      this.updateReview(review);
    } else {
      this.insertReview(review);
    }
  }

  public approveDtc(): void {
    if (!this.reviewable?.id) {
      return;
    }
    if (!this.reviewerRole) {
      return;
    }
    this.reviewRequestApi.approveDtc(this.reviewable.id, this.reviewerRole).subscribe({
      next: (reviewRequestResult) => {
        this.toast.success('Approval processed successfully');

        this.clearUnsavedChangesIfNothingChanged();
        this.reviewableUpdated.emit(this.reviewable);
        this.reviewable!.reviewRequest = undefined;
        this.displayedReview = undefined;
        if (reviewRequestResult?.affectedDtcSiblings?.length ?? 0 > 0) {
          this.approveSiblingsModal.openModal(
            this.reviewable!.id,
            reviewRequestResult?.affectedDtcSiblings,
            this.reviewerRole
          );
        }
        this.init();
      },
      error: (error) => {
        console.error('Error approving review:', errorToString(error));
      },
    });
  }

  public onDiscardReview(review: ReviewRequest): void {
    this.reviewToDiscard = review;
    this.discardReviewDecisionModal.openModal();
  }

  public onDiscardReviewDecision(decision: Decision): void {
    if (decision.result) {
      this.discardReview();
    }
  }

  public getCurrentReview(): ReviewRequest | undefined {
    const review = this.reviewable?.reviewRequest;
    if (review) {
      return review;
    } else if (!review && this.noReviewsExist() && this.reviewable) {
      const preReview = this.createReview();
      return preReview;
    } else {
      return undefined;
    }
  }

  protected changeRole(role: Event): void {
    const selectElement = role.target as HTMLSelectElement;
    const selectedRole = selectElement.value;

    const blocked = this.unsavedChangesService.areThereAnyUnsavedChanges(
      // leave
      () => {
        this.reviewerRole = this.previousReviewerRole = selectedRole;
      },
      // stay
      () => {
        selectElement.value = this.previousReviewerRole || '';
      }
    );

    if (!blocked) {
      this.previousReviewerRole = this.reviewerRole = selectedRole;
      this.displayReviewForSelectedRole();
    }
  }

  private noReviewsExist(): boolean {
    return ((this.reviewable?.reviewRequest ? 1 : 0) === 0 || !this.reviewable?.reviewRequest?.id) ?? true;
  }

  private discardReview(): void {
    if (!this.reviewToDiscard) {
      return;
    }

    this.reviewRequestApi.deleteReview(this.reviewToDiscard).subscribe({
      next: (reviewRequestResult) => {
        this.toast.success('Review deleted successfully');

        this.clearUnsavedChangesIfNothingChanged();
        this.reviewable!.reviewRequest = reviewRequestResult?.reviewRequest;
        this.displayedReview = undefined;
        this.reviewToDiscard = undefined;
        this.reviewableUpdated.emit(this.reviewable);
        this.deleteReviewPropagationModal.openModal(
          reviewRequestResult?.reviewRequest?.id,
          reviewRequestResult?.affectedDtcSiblings
        );
        this.init();
      },
      error: (error) => {
        console.error('Error deleting review:', errorToString(error));
      },
    });
  }

  private init(): void {
    if (!this.reviewable) {
      this.displayedReview = undefined;
      this.canApprove = false;
      return;
    }

    this.reviewable.reviewRequest = undefined; // get rid of old transient review requests
    this.displayedReview = undefined;
    this.fetchReviews();
  }

  private fetchReviewRoles(): void {
    this.reviewRequestApi.getReviewRoles().subscribe({
      next: (roles) => {
        this.reviewRoles = roles.map((role) => {
          return { id: role, selected: false, title: pascalCaseToWords(role) };
        });
        if (this.reviewRoles.length > 0) {
          this.previousReviewerRole = this.reviewerRole = this.reviewRoles[0].id;
        }
        this.init();
      },
      error: (error) => {
        console.error('Error fetching review roles:', errorToString(error));
      },
    });
  }

  private fetchReviews(): void {
    if (!this.reviewable?.id) {
      return;
    }
    this.reviewRequestApi.getReviews(this.reviewable.id).subscribe({
      next: (reviews) => {
        this.reviewable!.reviewRequest = reviews[0];
        this.createReviewDisabled = reviews.length > 0;
        this.displayReviewForSelectedRole();
      },
      error: (error) => {
        console.error('Error fetching reviews:', errorToString(error));
      },
    });
  }

  private displayReviewForSelectedRole(): void {
    if (this.changesSubscription) {
      this.changesSubscription.unsubscribe();
      this.changesSubscription = undefined;
    }

    this.displayHintReviewForOtherRoleExists = false;
    if (!this.reviewerRole) {
      this.canApprove = false;
      this.showCreateButton = false;
      this.editReviewEnabled = false;
    } else {
      this.setApproveButtonEnabled(this.reviewerRole);
      const review = this.reviewable!.reviewRequest;
      if (review) {
        if (review.reviewerRole != this.reviewerRole) {
          this.displayedReview = this.createReview();
          this.editReviewEnabled = false;
          this.displayHintReviewForOtherRoleExists = true;
        } else {
          this.displayedReview = review;
          this.editReviewEnabled = true;
        }
      } else {
        this.displayedReview = this.createReview();
        this.editReviewEnabled = true;
      }
      this.editReviewEnabled =
        this.editReviewEnabled && this.accessRightsMatchReviewRole(this.displayedReview.reviewerRole);
      this.showCreateButton = this.displayedReview?.id === undefined && this.editReviewEnabled;
    }
  }

  private accessRightsMatchReviewRole(reviewerRole: string | undefined): boolean {
    if (!reviewerRole) {
      return false;
    }

    switch (reviewerRole) {
      case ReviewerRole.AfterSales:
        return this.afterSalesRoleEnabled;
      case ReviewerRole.TechnicalOrganisation:
        return this.technicalOrganisationRoleEnabled;
      default:
        return false;
    }
  }

  private setApproveButtonEnabled(role: string): void {
    switch (role) {
      case ReviewerRole.AfterSales:
        this.canApprove = !this.reviewable?.reviewedByAfterSalesAt && this.afterSalesRoleEnabled;
        break;
      case ReviewerRole.TechnicalOrganisation:
        this.canApprove = !this.reviewable?.reviewedByTechnicalOrganisationAt && this.technicalOrganisationRoleEnabled;
        break;
      default:
        break;
    }
  }

  private insertReview(review: ReviewRequest): void {
    this.reviewRequestApi.insertDtcReview(review).subscribe({
      next: (reviewRequestResult) => {
        this.toast.success('Review stored successfully');

        this.refreshDisplayedReviewInList(reviewRequestResult.reviewRequest);
        this.displayReviewForSelectedRole();
        this.clearUnsavedChangesIfNothingChanged();
        this.reviewableUpdated.emit(this.reviewable);
        if (reviewRequestResult.affectedDtcSiblings && reviewRequestResult.affectedDtcSiblings.length > 0) {
          this.createReviewPropagationModal.openModal(
            reviewRequestResult?.reviewRequest?.id,
            reviewRequestResult?.affectedDtcSiblings
          );
        }
      },
      error: (error) => {
        console.error(errorToString(errorToString(error)));
      },
    });
  }

  private updateReview(review: ReviewRequest): void {
    this.reviewRequestApi.updateReview(review).subscribe({
      next: (reviewRequestResult) => {
        this.toast.success('Review stored successfully');
        this.refreshDisplayedReviewInList(reviewRequestResult.reviewRequest);
        this.displayReviewForSelectedRole();
        this.clearUnsavedChangesIfNothingChanged();
        this.reviewableUpdated.emit(this.reviewable);
        if (reviewRequestResult.affectedDtcSiblings && reviewRequestResult.affectedDtcSiblings.length > 0) {
          this.updateReviewPropagationModal.openModal(
            reviewRequestResult?.reviewRequest?.id,
            reviewRequestResult?.affectedDtcSiblings
          );
        }
      },
      error: (error) => {
        console.error(errorToString(errorToString(error)));
      },
    });
  }

  private createReview(): ReviewRequest {
    const newReview = {
      entityId: this.reviewable?.id,
      causeIntern: this.reviewable?.causeIntern,
      causeDealer: this.reviewable?.causeDealer,
      causeFarmer: this.reviewable?.causeFarmer,
      remedyIntern: this.reviewable?.remedyIntern,
      remedyDealer: this.reviewable?.remedyDealer,
      remedyFarmer: this.reviewable?.remedyFarmer,
      reviewerRole: this.reviewerRole,
      reviewNotes: '',
      changed: false,
    } as ReviewRequest;

    if (!this.reviewable!.reviewRequest) {
      this.reviewable!.reviewRequest = newReview;
    }
    this.createReviewDisabled = this.reviewable!.reviewRequest === undefined;

    return newReview;
  }

  private refreshDisplayedReviewInList(updatedReview?: ReviewRequest): void {
    if (!this.reviewerRole) {
      return;
    }

    if (!updatedReview) {
      return;
    }

    this.reviewable!.reviewRequest = updatedReview;
    this.displayedReview = updatedReview;
  }

  private clearUnsavedChangesIfNothingChanged(): void {
    const changedReview = this.reviewable!.reviewRequest?.changed;
    if (!changedReview) {
      this.unsavedChangesService.clearUnsavedChangesFromSessionStorage(STORAGE_KEYS.UNSAVED_DTC_CHANGES);
    }
  }
}
